import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
} from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { isGroupShape } from '@/lib/groups/group-kind'
import {
  type TextOnPathDraft,
  createTextOnPathDraft,
  textOnPathConfigToCommit,
  textOnPathConfigsEqual,
} from '@/lib/shapes/text-on-path-session'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Text on a Path mode's session: entering it for one object (closing node edit and crop first),
 * committing the draft on every dismissal, resolving the menubar/panel target, and committing when
 * a shape tool is picked.
 */
export function useTextOnPathSession({
  internalShapeManagerRef,
  selectedShape,
  textOnPathDraftRef,
  textOnPathShapeIdRef,
  setTextOnPathDraft,
  setTextOnPathShapeData,
  setTextOnPathLength,
  exitTextOnPathMode,
  nodeEditShapeIdRef,
  exitNodeEditMode,
  exitCropMode,
  onShapeCommitted,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  selectedShape: CenterCanvasConfig['selectedShape']
  textOnPathDraftRef: MutableRefObject<TextOnPathDraft | null>
  textOnPathShapeIdRef: MutableRefObject<string | null>
  setTextOnPathDraft: Dispatch<SetStateAction<TextOnPathDraft | null>>
  setTextOnPathShapeData: Dispatch<SetStateAction<ShapeData | null>>
  setTextOnPathLength: Dispatch<SetStateAction<number>>
  exitTextOnPathMode: () => void
  nodeEditShapeIdRef: MutableRefObject<string | null>
  exitNodeEditMode: (exitingShapeId: string | undefined) => void
  exitCropMode: () => void
  onShapeCommitted: CenterCanvasConfig['onShapeCommitted']
}) {
  /**
   * Modelled on `handleCropFrameCommit`: patch the manager, read the shape back LIVE, then raise
   * `onShapeCommitted` with a before/after so the change is both persisted and undoable. Reading
   * back matters — committing the enter-time `textOnPathShapeData` snapshot would write the
   * shape's stale geometry to the server if it moved while the bar was open.
   *
   * Returns `'manager-detached'`, leaving the draft in place, when there is no manager to commit to.
   */
  const commitTextOnPathDraft = useCallback((): 'done' | 'manager-detached' => {
    const draft = textOnPathDraftRef.current

    if (draft == null) return 'done'

    const mgr = internalShapeManagerRef.current

    if (!mgr) return 'manager-detached'

    const existing = mgr.getShapes().find((sh) => sh.id === draft.shapeId)

    // The shape can be gone (deleted, or the spread switched) — drop the draft.
    if (!existing) {
      exitTextOnPathMode()
      return 'done'
    }

    const next = textOnPathConfigToCommit(draft)

    // Nothing changed: no element write, and no undo entry the user would have to step past.
    if (textOnPathConfigsEqual(next, existing.textOnPath)) {
      exitTextOnPathMode()
      return 'done'
    }

    mgr.updateShape(draft.shapeId, { textOnPath: next })

    const updated = mgr.getShapes().find((sh) => sh.id === draft.shapeId)

    if (updated) {
      onShapeCommitted?.(updated, 'update', {
        before: { textOnPath: existing.textOnPath },
        after: { textOnPath: next },
      })
    }

    exitTextOnPathMode()
    return 'done'
  }, [exitTextOnPathMode, onShapeCommitted, textOnPathDraftRef, internalShapeManagerRef])

  /**
   * Close the bar and keep the text. This is dismiss-to-canvas — clicking away, switching tools,
   * or opening another single-shape mode.
   */
  const commitAndExitTextOnPathMode = useCallback(() => {
    if (commitTextOnPathDraft() === 'done') return

    // The manager handle is only briefly null: ShapeManager's `useImperativeHandle` has no deps,
    // so React detaches and re-attaches its ref on every commit, and `onSelectionChange` (a
    // layout effect inside ShapeManager) fires in between. Leaving group isolation clears the
    // selection in exactly that window, which used to drop the draft here and lose the text
    // (WMPCM-11613). Retry once the commit has finished; drop the draft only if the manager is
    // really gone (canvas unmounted).
    queueMicrotask(() => {
      if (commitTextOnPathDraft() === 'manager-detached') {
        exitTextOnPathMode()
      }
    })
  }, [commitTextOnPathDraft, exitTextOnPathMode])

  /**
   * Enters Text on a Path for one object (WMPCM-9514) — the context menus, the Object menu, and
   * the left panel's Path Operations button all land here.
   *
   * Deliberately carries NO lock/freeze policy of its own: the menu rows already gate on
   * `disabledItems.textOnPath`, and frozen (WMPCM-7632) and locked (WMPCM-9562) objects get their
   * own stories. Duplicating a rule here would silently pre-empt those decisions.
   */
  const tryEnterTextOnPathMode = useCallback(
    (shape: ShapeData): boolean => {
      if (isGroupShape(shape)) return false

      // Mutually exclusive with the other two single-shape canvas modes. Each of the three owns
      // a capture-phase Escape handler, so overlapping modes would let one keypress run two.
      // The exiting id is passed on, like every other node-edit exit: the parent uses it to run
      // `revertPathConversionIfUnedited`, so a preset that was auto-converted to a path just by
      // opening Edit Path is not left permanently converted for having been left via this door.
      const exitingNodeEditShapeId = nodeEditShapeIdRef.current

      exitNodeEditMode(exitingNodeEditShapeId ?? undefined)
      exitCropMode()

      // Seeded from the shape's existing text-on-path when it has one, so reopening the bar shows
      // what is already there instead of silently wiping it.
      setTextOnPathDraft(createTextOnPathDraft(shape))
      // Snapshot, like `cropShapeData`: it positions the floating bar. Moving the object while
      // the bar is open leaves the bar's DEFAULT placement stale (the on-canvas preview itself
      // tracks the live shape, and the bar can be dragged) — the same limitation crop mode has.
      setTextOnPathShapeData(shape)
      setTextOnPathLength(0)

      // Unlike crop mode we KEEP a selection rather than clearing it, and select the target if it
      // wasn't already (a right-click can target a shape without selecting it). Two reasons: the
      // canvas keeps its pointer events, so clicking away still reaches `onSelectionChange`, which
      // is what dismisses; and "the edited shape is the selection" is the invariant that dismiss
      // rule relies on. The blue chrome is hidden via `suppressChrome` instead, so it can't sit on
      // top of the red path.
      internalShapeManagerRef.current?.selectShapes([shape.id])

      return true
    },
    [
      exitNodeEditMode,
      exitCropMode,
      nodeEditShapeIdRef,
      setTextOnPathDraft,
      setTextOnPathShapeData,
      setTextOnPathLength,
      internalShapeManagerRef,
    ]
  )

  /**
   * The shape a menubar/panel invocation would act on: the current selection, if it is exactly
   * one non-group object. The context menus use `contextMenuTextOnPathTarget` instead, because
   * they also have a right-click target that can differ from the selection.
   */
  const resolveTextOnPathTargetId = useCallback((): string | null => {
    const mgr = internalShapeManagerRef.current
    const ids = mgr?.getSelectedShapeIds() ?? []

    if (ids.length !== 1) return null

    const shape = mgr?.getShapes().find((s) => s.id === ids[0])

    return shape == null || isGroupShape(shape) ? null : shape.id
  }, [internalShapeManagerRef])

  /** Entry point used by every menu surface: resolve the id, then enter. */
  const enterTextOnPathModeById = useCallback(
    (shapeId: string): boolean => {
      const shape = internalShapeManagerRef.current?.getShapes().find((s) => s.id === shapeId)

      if (!shape) return false

      return tryEnterTextOnPathMode(shape)
    },
    [tryEnterTextOnPathMode, internalShapeManagerRef]
  )

  // Text on a Path: picking a shape tool means "draw something new", not "keep editing this" —
  // the same intent signal node-edit and crop mode both exit on. Commits rather than discards,
  // matching every other dismissal that isn't Escape.
  useEffect(() => {
    if (selectedShape != null && textOnPathShapeIdRef.current != null) {
      commitAndExitTextOnPathMode()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShape])

  return {
    commitAndExitTextOnPathMode,
    tryEnterTextOnPathMode,
    resolveTextOnPathTargetId,
    enterTextOnPathModeById,
  }
}
